// QueryDslHelp — the "📖 DSL" help modal shared by the Query tab and Fleet
// Query: documents the query-area extras that aren't plain SQL (the `expand`
// batch-generation statement, editor snippets, @parameters), so the features
// are discoverable without reading the changelog. Static content; keep this in
// sync when the DSL grows.
import Modal from './Modal.jsx';

export default function QueryDslHelp(props) {
  return (
    <Modal open={props.open} onClose={props.onClose} title="📖 Query DSL & editor extras" wide>
      <div class="query-dsl-help">
        <h3><code>expand</code> — generate one statement per row</h3>
        <p>
          Write an <code>expand</code> statement anywhere in the editor and run it like any
          other statement. It first runs <code>src</code>, then emits one copy of{' '}
          <code>target</code> per result row, replacing each <code>&amp;ColumnName</code> with
          that row's value — the generated statements then run in order, exactly as if you
          had typed them.
        </p>
        <pre class="query-dsl-help-pre">{`expand src="SELECT NoJual, Divisi FROM tr_jual_m WHERE Tgl >= :TglStart#date#",
       target="CALL ProsesJurnal(&NoJual, &Divisi);"

-- with 2 matching rows this becomes, and runs as:
CALL ProsesJurnal('25000234', 'MKM');
CALL ProsesJurnal('25000235', 'SPK');`}</pre>
        <ul>
          <li><code>src</code> is any query, run normally first — including <code>:parameter</code> prompting.</li>
          <li><code>&amp;Col</code> matches src's columns case-insensitively. A <code>&amp;Col</code> that matches no column fails the whole expand up front, listing src's real column names — nothing runs.</li>
          <li>Values are substituted safely for any column type: strings are single-quoted and escaped, numeric columns stay bare, <code>NULL</code> becomes the keyword <code>NULL</code>. Zero src rows generate zero statements (not an error).</li>
          <li>The <code>src="…"</code> / <code>target="…"</code> attribute values use <code>\"</code> (or <code>\'</code>) to escape their own quote, and <code>\\</code> for a literal backslash.</li>
          <li>Works in Fleet Query too: in free mode, prefix it like any statement — <code>[connection] expand …</code> — and src runs on that statement's own connection; in Connection-preset mode each checked target runs src against its <em>own</em> data and generates its own statements.</li>
        </ul>

        <h3>Editor snippets</h3>
        <p>
          Type a mnemonic and pick it from autocomplete to insert a template —{' '}
          <kbd>Tab</kbd> jumps between the placeholders:{' '}
          <code>sel</code>, <code>sela</code>, <code>count</code>, <code>ins</code>,{' '}
          <code>upd</code>, <code>del</code>, <code>joinon</code>, <code>cte</code>,{' '}
          <code>case</code>, and <code>expand</code>.
        </p>

        <h3>:parameters</h3>
        <p>
          Write <code>:Name</code> (or <code>:Name#date#</code> / <code>#number#</code> to pick
          the input widget) anywhere outside a string, and running prompts once for each
          distinct name — the value is bound injection-safe as <code>@Name</code>, shared by
          every statement in the run. Colons inside strings ('12:30:00') and comments are
          never treated as parameters.
        </p>
      </div>
    </Modal>
  );
}
